import { logger } from '@mail-core/logger';
import { parseJSONSafe } from '@mail-core/std';
import { CancelFilesMigrationAPICall } from 'reactApp/api/filesMigration/CancelFilesMigrationAPICall';
import { ProfileFullApiCall } from 'reactApp/api/profile/ProfileFullAPICall';
import {
    cloudFilesMigrationDesktop,
    profilePollingInterval,
} from 'reactApp/appHelpers/featuresHelpers/features/cloudFilesMigrationDesktop';
import {
    cancelFilesMigraionFail,
    cancelFilesMigraionSuccess,
    cancelFilesMigrationRequest,
    updateMigrationState,
} from 'reactApp/modules/filesMigration/filesMigration.module';
import { hasActiveMigration, hasWaitForCancel, hasWaitForMigration } from 'reactApp/modules/filesMigration/filesMigration.selectors';
import { type FilesMigrationProfileResponse, FilesMigrationStatus } from 'reactApp/modules/filesMigration/filesMigration.types';
import { loggerSaga } from 'reactApp/modules/logger/logger.saga';
import { DEVICE_ID_WEB } from 'reactApp/modules/profile/profile.contants';
import { handleLoadUserFullProfile } from 'reactApp/modules/profile/profile.saga';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { call, delay, fork, put, select, takeEvery } from 'typed-redux-saga';

const getFilesMigrationStatusFromProfile = async () => {
    try {
        const { data: profileResponse } = await new ProfileFullApiCall().makeRequest({ did: DEVICE_ID_WEB });

        if (profileResponse?.code !== 'OK') {
            return;
        }

        if (!profileResponse.profile?.properties?.['tg.status']) {
            return;
        }

        return parseJSONSafe<Partial<FilesMigrationProfileResponse>>(profileResponse.profile?.properties?.['tg.status'], {});
    } catch (error) {
        logger.error(error);
    }
};

function* pollMigrationStatus() {
    while (true) {
        const migrationData = yield* call(getFilesMigrationStatusFromProfile);

        if (migrationData) {
            yield put(
                updateMigrationState({
                    allChatsCount: migrationData.all_chats_count ?? 0,
                    migratedChatsCount: migrationData.migrated_count ?? 0,
                    status: migrationData.migration_status as FilesMigrationStatus,
                    migratedBytes: migrationData.migration_current_size ?? 0,
                })
            );

            if (
                !(
                    migrationData.migration_status === FilesMigrationStatus.inProgress ||
                    migrationData.migration_status === FilesMigrationStatus.authed ||
                    migrationData.migration_status === FilesMigrationStatus.waitTakeout ||
                    migrationData.migration_status === FilesMigrationStatus.cancelInProgress
                )
            ) {
                return;
            }
        }

        yield delay(profilePollingInterval);
    }
}

const cancelFilesMigraion = () => new CancelFilesMigrationAPICall().makeRequest();

export function* initFilesMigration() {
    if (!cloudFilesMigrationDesktop) {
        return;
    }

    yield* handleLoadUserFullProfile(false, true, false);

    const isActiveMigration = yield* select(hasActiveMigration);
    const isWaitForMigration = yield* select(hasWaitForMigration);
    const isWaitForCancel = yield* select(hasWaitForCancel);

    if (!(isActiveMigration || isWaitForMigration || isWaitForCancel)) {
        return;
    }

    yield fork(pollMigrationStatus);
}

function* handleCancelFilesMigration() {
    try {
        yield* call(cancelFilesMigraion);
        yield put(cancelFilesMigraionSuccess());
    } catch (error: any) {
        yield put(cancelFilesMigraionFail());

        loggerSaga({ error });

        snackbarController.showSnackbar({
            id: 'cancel-files-migration-error',
            closable: true,
            text: 'Не удалось остановить загрузку файлов. Повторите попытку позже',
            type: SnackbarTypes.failure,
        });
    }
}

export function* watchFilesMigration() {
    yield takeEvery(cancelFilesMigrationRequest.toString(), handleCancelFilesMigration);
}
